import React from 'react';
import ReactDOM from 'react-dom';
import Print from '../components/Print';
import Fab from '../components/Fab';

import { calculateAltitudeGainLoss } from '../utils/utils.js';
import { attributes } from '../components/TrailSearch/attributes';
import layerTitleConfig from 'configs/AppLayers.json';

import WebMap from '@arcgis/core/WebMap';
import MapView from '@arcgis/core/views/MapView';
import Basemap from '@arcgis/core/Basemap';
import BasemapGallery from '@arcgis/core/widgets/BasemapGallery';
import Bookmark from '@arcgis/core/webmap/Bookmark';
import Extent from '@arcgis/core/geometry/Extent';
import Graphic from '@arcgis/core/Graphic';
import Polyline from '@arcgis/core/geometry/Polyline';
import SpatialReference from '@arcgis/core/geometry/SpatialReference';
import Bookmarks from '@arcgis/core/widgets/Bookmarks';
import Expand from '@arcgis/core/widgets/Expand';
import VectorTileLayer from '@arcgis/core/layers/VectorTileLayer';
import { whenOnce } from '@arcgis/core/core/reactiveUtils';
import * as watchUtils from '@arcgis/core/core/watchUtils';

// Object to handle module level variables
const app = {};

// Highlighted trail segments
const highlights = {};
const poiHighlights = [];

// Layer titles
const TRAILHEAD_TITLE = layerTitleConfig.TRAILHEAD_TITLE;
const TRAIL_DISSOLVED_MIDSCALE_TITLE = layerTitleConfig.TRAIL_DISSOLVED_MIDSCALE_TITLE;
const TRAIL_DISSOLVED_STATEWIDE_TITLE = layerTitleConfig.TRAIL_DISSOLVED_STATEWIDE_TITLE;
const TRAIL_DISSOLVED_TITLE = layerTitleConfig.TRAIL_DISSOLVED_TITLE;
const TRAIL_DISSOLVED_TITLE_COPY = layerTitleConfig.TRAIL_DISSOLVED_TITLE_COPY;
const FEATURED_ROUTES_TITLE = layerTitleConfig.FEATURED_ROUTES;
const FEATURED_ROUTES_COPY_TITLE = layerTitleConfig.FEATURED_ROUTES_COPY;
const TRAIL_CLOSED_TITLE = layerTitleConfig.TRAIL_CLOSED_TITLE;

/**
 * Initialize the WebMap used in the application
 * @returns Promise<`esri/WebMap`>
 */
export async function initWebMap(webmapId) {
  const webmap = new WebMap({
    portalItem: {
      id: webmapId,
    },
  });

  app.webmap = webmap;

  // Parse locally saved bookmarks
  const bookmarksLocal = JSON.parse(localStorage.getItem('trail-bookmarks')) || [];

  webmap.bookmarks = bookmarksLocal.map((a) => Bookmark.fromJSON(a));

  await webmap.loadAll();

  //Turn on POI layers - scaled
  let poiLayer;
  for (let { title, visibilityValue } of Object.values(layerTitleConfig.POI_SCALED)) {
    //poiLayer = webmap.findLayerById(id);
    poiLayer = webmap.allLayers.find(function (layer) {
      return layer.title === title;
    });
    poiLayer.visible = true;
    poiLayer.popupEnabled = false;
    await poiLayer.load();
    setScaleSymbols(poiLayer, visibilityValue);
  }

  //Turn on dissolved trail, turn off its popup
  const trailDissolvedLayer = webmap.allLayers.find(function (layer) {
    return layer.title === TRAIL_DISSOLVED_TITLE;
  });
  trailDissolvedLayer.visible = true;
  trailDissolvedLayer.popupEnabled = false;
  trailDissolvedLayer.outFields = ['*'];

  await trailDissolvedLayer.load();
  //Build renderer , white background with 5px , foreground with grey dash line of 2px , using well known color names

  //Turn on closed trail, turn off its popup
  const trailClosedLayer = webmap.allLayers.find(function (layer) {
    return layer.title === TRAIL_CLOSED_TITLE;
  });
  trailClosedLayer.outFields = ['*'];
  trailClosedLayer.visible = true;
  trailClosedLayer.popupEnabled = false;
  await trailClosedLayer.load();

  //Turn off dissolved trail copy, turn off its popup
  const trailDissolvedLayerCopy = webmap.allLayers.find(function (layer) {
    return layer.title === TRAIL_DISSOLVED_TITLE_COPY;
  });
  trailDissolvedLayerCopy.outFields = ['*'];
  trailDissolvedLayerCopy.visible = true;
  trailDissolvedLayerCopy.popupEnabled = false;
  await trailDissolvedLayerCopy.load();

  //Turn off statewide dissolved trail copy, turn off its popup
  const trailDissolvedStatewideLayerCopy = webmap.allLayers.find(function (layer) {
    return layer.title === TRAIL_DISSOLVED_STATEWIDE_TITLE;
  });
  trailDissolvedStatewideLayerCopy.visible = true;
  trailDissolvedStatewideLayerCopy.popupEnabled = false;
  trailDissolvedStatewideLayerCopy.outFields = ['*'];
  await trailDissolvedStatewideLayerCopy.load();

  //Turn off midscale dissolved trail copy, turn off its popup
  const trailDissolvedMidscaleLayerCopy = webmap.allLayers.find(function (layer) {
    return layer.title === TRAIL_DISSOLVED_MIDSCALE_TITLE;
  });
  trailDissolvedMidscaleLayerCopy.visible = true;
  trailDissolvedMidscaleLayerCopy.popupEnabled = false;
  trailDissolvedMidscaleLayerCopy.outFields = ['*'];
  await trailDissolvedMidscaleLayerCopy.load();

  //Turn off dissolved featured routes, turn off its popup
  const featuredRoutesLayer = webmap.allLayers.find(function (layer) {
    return layer.title === FEATURED_ROUTES_TITLE;
  });
  featuredRoutesLayer.visible = true;
  featuredRoutesLayer.popupEnabled = false;
  await featuredRoutesLayer.load();

  //Turn off dissolved featured routes, turn off its popup
  const featuredRoutesCopyLayer = webmap.allLayers.find(function (layer) {
    return layer.title === FEATURED_ROUTES_COPY_TITLE;
  });
  featuredRoutesCopyLayer.visible = true;
  featuredRoutesCopyLayer.popupEnabled = false;
  await featuredRoutesCopyLayer.load();

  //Turn on Trailhead layer
  const trailheadLayer = webmap.allLayers.find(function (layer) {
    return layer.title === TRAILHEAD_TITLE;
  });
  trailheadLayer.visible = true;
  trailheadLayer.popupEnabled = false;
  await trailheadLayer.load();

  return webmap;
}

const setScaleSymbols = (layer, value) => {
  //create if statement to change stop's values depending on poi layer
  layer.renderer.visualVariables = [
    {
      type: 'size',
      valueExpression: '$view.scale',
      stops: [
        {
          size: 23,
          value: value,
        },
        {
          size: 0,
          value: value + 1,
        },
      ],
    },
  ];
};

/**
 * Initialize the MapView for the application
 * @param {`esri/views/MapView`} view
 * @param {HTMLElement} searchContainer
 * @returns Promise<void>
 */
export async function initView(container) {
  const view = new MapView({
    container,
    map: app.webmap,
  });

  const bookmarks = new Bookmarks({
    view: view,
    editingEnabled: true,
    bookmarkCreationOptions: {
      takeScreenshot: true,
      captureExtent: true,
      screenshotSettings: {
        width: 100,
        height: 100,
      },
    },
  });

  const bookmarksExpand = new Expand({
    content: bookmarks,
  });

  const printNode = document.createElement('div');
  ReactDOM.render(<Print view={view} />, printNode);
  const printExpand = new Expand({
    content: printNode,
    expandIconClass: 'esri-icon-printer',
  });

  const fabNode = document.createElement('div');
  view.ui.add(fabNode, 'bottom-right');
  ReactDOM.render(<Fab view={view} style={{ boxShadow: 'unset !important' }} />, fabNode);

  const vectorTileLayer = new VectorTileLayer({
    portalItem: {
      id: '170e5c60f3764eeb96f93dc65bb09b6a', // Forest and Parks Canvas
    },
    opacity: 0.75,
  });
  const basemap = new Basemap({
    baseLayers: [vectorTileLayer],
    title: 'DETOUR',
  });
  const basemapGallery = new BasemapGallery({
    view: view,
    container: document.createElement('div'),
    source: [
      basemap,
      Basemap.fromId('satellite'),
      Basemap.fromId('hybrid'),
      Basemap.fromId('topo-vector'),
      Basemap.fromId('streets'),
      Basemap.fromId('dark-gray-vector'),
      Basemap.fromId('gray-vector'),
    ],
  });
  const basemapGalleryExpand = new Expand({
    view: view,
    content: basemapGallery.container,
    expandIconClass: 'esri-icon-basemap',
  });

  // Add the expand instance to the ui

  view.ui.add(basemapGalleryExpand, 'bottom-left');
  view.ui.add(bookmarksExpand, 'top-right');
  view.ui.add(printExpand, 'top-right');

  // view.highlightOptions = {
  //   // haloColor: '#55ff00',
  //   color: '#55ff00'
  // };
  view.highlightOptions = {
    color: [85, 255, 0, 1],
    haloOpacity: 0.9,
    fillOpacity: 0.2,
  };
  app.view = view;

  app.view.when(() => {
    // set up bookmarks
    bookmarks.bookmarks.on('change', ({ added }) => {
      // Save bookmarks to local storage
      const bookmarksJson = added.map((x) => x.toJSON());
      let bookmarkStored = JSON.parse(localStorage.getItem('trail-bookmarks')) || [];
      localStorage.removeItem('trail-bookmarks');
      bookmarkStored = bookmarkStored.concat(bookmarksJson);
      localStorage.setItem('trail-bookmarks', JSON.stringify(bookmarkStored));
    });

    // addSearch(searchContainer, view);

    view.ui.move('zoom', 'top-right');
  });

  app.view.popup.on('trigger-action', ({ action }) => {
    if (action.id === 'query-elevation') {
      const { selectedFeature } = app.view.popup;
      app.elevationLayer.queryElevation(selectedFeature.geometry).then((result) => {
        console.log(calculateAltitudeGainLoss(result.geometry.paths));
      });
    }
  });

  // Set the extent on the view on Ohio
  view.extent = new Extent({
    xmin: -85.2154541015625,
    ymin: 37.24365234375,
    xmax: -79.9969482421875,
    ymax: 43.20947265625,
  });

  await view.when();
  await view.map.load();

  return view;
}

export async function fetchTrailsInExtent(view) {
  view = view || app.view;
  if (!view && !app.view) return;
  if (!view.stationary) {
    await whenOnce(() => view.stationary);
  }

  await view.when();
  await view.map.load();

  const layer = view.map.allLayers.find((layer) => {
    if (view.scale < 250000) {
      return layer.title === TRAIL_DISSOLVED_TITLE;
    } else if (view.scale >= 250000 && view.scale <= 1000000) {
      return layer.title === TRAIL_DISSOLVED_MIDSCALE_TITLE;
    } else {
      return layer.title === TRAIL_DISSOLVED_STATEWIDE_TITLE;
    }
  });
  const query = layer.createQuery();

  query.geometry = view.extent.clone();
  query.outFields = ['*'];
  query.returnGeometry = false;
  query.orderByFields = 'Length_SPC DESC';

  // Limit results to 2000 at a time
  query.num = 2000;
  const { features } = await layer.queryFeatures(query);
  //const uniqueFeatures = uniqBy(features, (feature) => feature.attributes.PRIMARY_NAME);
  //return uniqueFeatures;
  return features;
}

export async function fetchTrailsBySearchTerm(view, searchTerm) {
  view = view || app.view;
  if (!view && !app.view) return;
  if (!view.stationary) {
    await whenOnce(() => view.stationary);
  }

  await view.when();
  await view.map.load();

  const layer = view.map.allLayers.find((layer) => {
    if (view.scale < 250000) {
      return layer.title === TRAIL_DISSOLVED_TITLE;
    } else if (view.scale >= 250000 && view.scale <= 1000000) {
      return layer.title === TRAIL_DISSOLVED_MIDSCALE_TITLE;
    } else {
      return layer.title === TRAIL_DISSOLVED_STATEWIDE_TITLE;
    }
  });

  const query = layer.createQuery();

  query.where = searchTerm;
  query.outFields = ['*'];
  query.returnGeometry = false;
  query.orderByFields = 'Length_SPC DESC';
  // TODO Length_SPC DESC
  // Limit results to 2000 at a time
  query.num = 2000;
  const { features } = await layer.queryFeatures(query);
  //const uniqueFeatures = uniqBy(features, (feature) => feature.attributes.PRIMARY_NAME);
  //return uniqueFeatures;
  return features;
}

/**
 * Filters map based on user clicking a trail segment
 * @param {Array} features
 * @returns Promise<void>
 */
export async function filterMapDataForTrailSelected(features) {
  let featureIsFiltered = Object.keys(highlights).some((trailId) => trailId == features[0].attributes.PRIMARY_TRAIL_ID);

  if (featureIsFiltered) {
    highlights[features[0].attributes.PRIMARY_TRAIL_ID].removeHandler.remove();
    highlights[features[0].attributes.PRIMARY_TRAIL_ID].on = false;
  }
  for (let i = 0; i < features.length; ++i) {
    let greenLine; // Green line is only used on a trail that is selected and filtered
    let greenLineGraphic;
    if (featureIsFiltered) {
      //Check if trail feature is selected and filtered/highlighted
      greenLine = {
        type: 'simple-line', // autocasts as new SimpleLineSymbol()
        color: '#55ff00',
        width: '15px',
        style: 'solid',
      };
    }

    let pinkLine = {
      type: 'simple-line', // autocasts as new SimpleLineSymbol()
      color: '#D111D1',
      width: '8px',
      style: 'solid',
    };

    let whiteLine = {
      type: 'simple-line', // autocasts as new SimpleLineSymbol()
      color: '#ffffff',
      width: '2px',
      style: 'solid',
    };

    let whiteLineGraphic = new Graphic({
      geometry: features[i].geometry, // Add the geometry created in step 4
      symbol: whiteLine, // Add the symbol created in step 5
      attributes: features[i].attributes, // Add the attributes created in step 6
    });

    let pinkLineGraphic = new Graphic({
      geometry: features[i].geometry, // Add the geometry created in step 4
      symbol: pinkLine, // Add the symbol created in step 5
      attributes: features[i].attributes, // Add the attributes created in step 6
    });

    if (featureIsFiltered) {
      greenLineGraphic = new Graphic({
        geometry: features[i].geometry, // Add the geometry created in step 4
        symbol: greenLine, // Add the symbol created in step 5
        attributes: features[i].attributes, // Add the attributes created in step 6
      });
    }

    // // Add the graphic to the view's default graphics view
    // // If adding multiple graphics, use addMany and pass in the array of graphics.
    app.view.graphics.removeAll();
    if (featureIsFiltered) {
      app.view.graphics.add(greenLineGraphic);
    }
    app.view.graphics.add(pinkLineGraphic);
    app.view.graphics.add(whiteLineGraphic);
  }
  //highlightTrailsBySelection(primaryTrailName, layer, layerView);
}

/**
 * Filters map based on user selecting a featured route
 * @param {Array} feature
 * @returns Promise<void>
 */
export async function filterMapDataForRouteSelected(feature) {
  // let featureIsFiltered = Object.keys(highlights).some(trailId => trailId == features[0].attributes.PRIMARY_TRAIL_ID)
  // if(featureIsFiltered){
  //   highlights[features[0].attributes.PRIMARY_TRAIL_ID].removeHandler.remove();
  //   highlights[features[0].attributes.PRIMARY_TRAIL_ID].on = false;
  // }
  // for(let i = 0; i < features.length; ++i){
  // let greenLine; // Green line is only used on a trail that is selected and filtered
  // let greenLineGraphic;
  // if(featureIsFiltered){ //Check if trail feature is selected and filtered/highlighted
  //   greenLine = {
  //     type: "simple-line",  // autocasts as new SimpleLineSymbol()
  //     color: "#55ff00",
  //     width: "15px",
  //     style: "solid"
  //   };
  // }

  let blueLine = {
    type: 'simple-line', // autocasts as new SimpleLineSymbol()
    color: '#025373',
    width: '8px',
    style: 'solid',
  };

  let whiteLine = {
    type: 'simple-line', // autocasts as new SimpleLineSymbol()
    color: '#ffffff',
    width: '2px',
    style: 'solid',
  };

  let whiteLineGraphic = new Graphic({
    geometry: feature.geometry, // Add the geometry created in step 4
    symbol: whiteLine, // Add the symbol created in step 5
    attributes: feature.attributes, // Add the attributes created in step 6
  });

  let blueLineGraphic = new Graphic({
    geometry: feature.geometry, // Add the geometry created in step 4
    symbol: blueLine, // Add the symbol created in step 5
    attributes: feature.attributes, // Add the attributes created in step 6
  });

  // if(featureIsFiltered){
  //   greenLineGraphic = new Graphic({
  //     geometry: features[i].geometry, // Add the geometry created in step 4
  //     symbol: greenLine, // Add the symbol created in step 5
  //     attributes: features[i].attributes, // Add the attributes created in step 6
  //   });
  // }

  // // Add the graphic to the view's default graphics view
  // // If adding multiple graphics, use addMany and pass in the array of graphics.
  app.view.graphics.removeAll();
  // if(featureIsFiltered){
  //   app.view.graphics.add(greenLineGraphic);
  // }
  app.view.graphics.add(blueLineGraphic);
  app.view.graphics.add(whiteLineGraphic);

  //};
  //highlightTrailsBySelection(primaryTrailName, layer, layerView);
}

/**
 * Filters map based on user clicking a POI
 * @param {String} objectid
 * @returns Promise<void>
 */
export async function filterMapDataForPoiSelected(objectid, layer) {
  if (!app.webmap) return;

  await app.webmap.load();
  // const layer = app.webmap.findLayerById(NEW_POI_ID);
  layer.outFields = ['*'];
  await layer.load();
  await app.view.when();
  const layerView = await app.view.whenLayerView(layer).catch((err) => console.log(err.message));
  if (!layerView) return;

  highlightPoiBySelection(objectid, layer, layerView);
}

/**
 * Highlight selected poi by user click
 * @param {String} objectid
 * @param {Layer} layer
 * @param {LayerView} layerView
 * @returns undefined
 */
const highlightPoiBySelection = (objectid, layer, layerView) => {
  if (objectid) {
    let query = layer.createQuery();
    query.where = `OBJECTID = ${objectid}`;
    layer.queryFeatures(query).then(function (result) {
      for (let i = 0; i < poiHighlights.length; ++i) {
        poiHighlights[i].remove();
      }
      poiHighlights.push(layerView.highlight(result.features));
    });
  }
  return;
};

/**
 * Highlight filtered trails by user click
 * @param {String} primaryTrailName
 * @param {Layer} layer
 * @param {LayerView} layerView
 * @returns undefined
 */
export const highlightTrailsBySelection = (primaryTrailName, layer, layerView) => {
  if (primaryTrailName) {
    let query = layer.createQuery();
    query.where = `PRIMARY_NAME = '${primaryTrailName.replace("'", "''")}'`;
    layer.queryFeatures(query).then(function (result) {
      for (let i = 0; i < highlights.length; ++i) {
        highlights[i].remove();
      }
      highlights.push(layerView.highlight(result.features));
    });
  }
  return;
};
/**
 * Filters map based on primary names
 * @param {Number[]} trailIds
 * @param {MapView} view
 * @param {Boolean} trailSelected
 * @returns Promise<void>
 */
export async function filterMapDataByTrailIds(trailIds, view, trailSelected = null) {
  if (!app.webmap) return;

  const where = `PRIMARY_TRAIL_ID in ('${trailIds.join("','")}')`;

  await app.webmap.load();
  const layer = app.webmap.allLayers.find(function (layer) {
    return layer.title === TRAIL_DISSOLVED_TITLE;
  });
  layer.outFields = ['*'];
  await layer.load();
  app.view = view;
  await app.view.when();
  const layerView = await app.view.whenLayerView(layer).catch((err) => console.log(err.message));
  if (!layerView) return;

  await watchUtils.whenFalseOnce(layerView, 'updating');
  let query = layer.createQuery();
  query.outFields = ['*'];
  query.where = where;
  app.webmap.basemap.visible = false;

  await view.whenLayerView(layer).then(function (featureLayerView) {
    featureLayerView.effect = {
      filter: {
        where: where,
      },
      excludedEffect: 'opacity(50%)',
    };
  });
  highlightFilteredTrails(layer, layerView, query, trailSelected);
}

/**
 * Highlights filtered trails
 * @param {Object} layer
 * @param {Object} layerView
 * @param {String} query
 * @param {Boolean} trailSelected
 * @returns undefined
 */
const highlightFilteredTrails = (layer, layerView, query, trailSelected) => {
  layer.queryFeatures(query).then(function (result) {
    for (let trailId of Object.keys(highlights)) {
      highlights[trailId].removeHandler.remove();
      delete highlights[trailId];
    }
    for (let i = 0; i < result.features.length; ++i) {
      highlights[result.features[i].attributes.PRIMARY_TRAIL_ID] = {
        removeHandler: layerView.highlight(result.features[i]),
        trailFeature: result.features[i],
        on: true,
      };
    }

    if (trailSelected) {
      filterMapDataForTrailSelected([trailSelected]);
    }
  });
  return;
};

/**
 * Turn on highlights for trails that are off
 * @returns undefined
 */
export const turnOnHighlights = async () => {
  const layer = app.webmap.allLayers.find(function (layer) {
    return layer.title === TRAIL_DISSOLVED_TITLE;
  });
  layer.outFields = ['*'];
  await layer.load();
  await app.view.when();
  const layerView = await app.view.whenLayerView(layer).catch((err) => console.log(err.message));
  if (!layerView) return;

  for (let trailId of Object.keys(highlights)) {
    if (!highlights[trailId].on) {
      highlights[trailId].removeHandler = layerView.highlight(highlights[trailId].trailFeature);
      highlights[trailId].on = true;
    }
  }
};

/**
 * Returns where clause to highlight matching advanced options features
 * @param {Object} advancedOptions
 * * @param {String} primaryTrailName
 * @returns String
 */
export const filterAdvancedOptions = (advancedOptions, primaryTrailName) => {
  let where = `PRIMARY_NAME = '${primaryTrailName.replace("'", '')}'`;
  if (advancedOptions) {
    for (const [key, vals] of Object.entries(advancedOptions)) {
      for (const [i, val] of vals.entries()) {
        if (i === 0) {
          where += ` AND (${attributes[key].name} = '${attributes[key].values[val]}'`;
        } else {
          where += ` OR ${attributes[key].name} = '${attributes[key].values[val]}'`;
        }
      }
      where += ')';
    }
  }
  return where;
};

/**
 * Returns true for valid POI title
 * @param {String} title
 * @returns Boolean
 */
export const isPoiId = (title) => {
  return Object.values(layerTitleConfig.POI_SCALED).some((poi) => {
    console.log(poi.title);
    return poi.title === title;
  });
};

/**
 * Returns true for valid Trailhead title
 * @param {String} title
 * @returns Boolean
 */
export const isTrailheadId = (title) => {
  return layerTitleConfig.TRAILHEAD_TITLE === title;
};

/**
 * Returns a single feature (with all attributes), based on trail object id
 * @param {String} fid
 * @returns Promise<void>
 */
export async function getTrailFeature(trailName) {
  const layer = app.webmap.allLayers.find(function (layer) {
    return layer.title === TRAIL_DISSOLVED_TITLE;
  });
  const query = layer.createQuery();
  query.outFields = ['*'];
  query.where = `UNIQUE_NAME = '${trailName.replace("'", '')}'`;
  const { features } = await layer.queryFeatures(query);
  return features;
}

/**
 * Returns one or more features based on a trail id
 * @param {Number} trailId
 * @returns Promise<void>
 */
export async function getAllTrailFeaturesByTrailId(trailId, viewSpatialReference) {
  const layer = app.webmap.allLayers.find(function (layer) {
    return layer.title === TRAIL_DISSOLVED_TITLE;
  });

  const query = layer.createQuery();
  query.outFields = ['*'];
  query.where = `PRIMARY_TRAIL_ID = ${trailId}`;
  query.outSpatialReference = viewSpatialReference;
  const { features } = await layer.queryFeatures(query);
  return features;
}

/**
 * Returns one or more features based on a managed area name
 * @param {String} managedAreaName
 * @returns Promise<void>
 */
export async function getAllTrailFeaturesByManagedAreaName(managedAreaName) {
  const layer = app.webmap.allLayers.find(function (layer) {
    return layer.title === TRAIL_DISSOLVED_TITLE;
  });
  const query = layer.createQuery();
  query.outFields = ['*'];
  query.where = `MANAGED_AREA_NAME = '${managedAreaName.replace("'", '')}'`;
  query.orderByFields = 'Length_SPC DESC';

  const { features } = await layer.queryFeatures(query);
  return features;
}

/**
 * Returns one or more features based on a manager
 * @param {String} manager
 * @returns Promise<void>
 */
export async function getAllTrailFeaturesByManager(manager) {
  const layer = app.webmap.allLayers.find(function (layer) {
    return layer.title === TRAIL_DISSOLVED_TITLE;
  });
  const query = layer.createQuery();
  query.outFields = ['*'];
  query.where = `MANAGER = '${manager}'`;
  query.orderByFields = 'Length_SPC DESC';

  const { features } = await layer.queryFeatures(query);
  return features;
}

/**
 * Returns one or more features based on a geometry and trail Id
 * @param {Object} geometry
 * @param {Number} trailId
 * @returns Promise<void>
 */
export async function getAllNearbyTrails(geometry, trailId) {
  const layer = app.webmap.allLayers.find(function (layer) {
    return layer.title === TRAIL_DISSOLVED_TITLE;
  });
  const query = layer.createQuery();

  query.geometry = geometry;
  query.where = `NOT PRIMARY_TRAIL_ID = ${trailId}`;
  query.units = 'feet';
  query.distance = 500;
  query.spatialRelationship = 'intersects';
  query.outFields = ['*'];

  let features;
  try {
    const response = await layer.queryFeatures(query);
    features = { response };
  } catch (error) {
    console.log(error);
    features = [];
  }

  return features;
}

/**
 * Returns one or more features based on a trail name
 * @param {String} objectid
 * @returns Promise<void>
 */
export async function getPoi(objectid, layer) {
  const query = layer.createQuery();
  query.outFields = ['*'];
  query.where = `OBJECTID = ${objectid}`;
  const { features } = await layer.queryFeatures(query);
  return features;
}

/**
 * Removes all highlights
 * @returns undefined
 */
export async function turnOffHighlights(view, trailSelected = null) {
  if (app.webmap) {
    await app.webmap.load();
    const layer = app.webmap.allLayers.find(function (layer) {
      return layer.title === TRAIL_DISSOLVED_TITLE;
    });
    layer.outFields = ['*'];
    await layer.load();
    app.view = view;
    await app.view.when();

    const layerView = await app.view.whenLayerView(layer).catch((err) => console.log(err.message));
    if (!layerView) return;

    await watchUtils.whenFalseOnce(layerView, 'updating');
    for (let trailId of Object.keys(highlights)) {
      highlights[trailId].removeHandler.remove();
      delete highlights[trailId];
    }
    if (trailSelected) {
      //This will turn off the trail highlight if it was highlighted previously
      await filterMapDataForTrailSelected([trailSelected]);
    }
  }
}

/**
 * Get all favorited trails from dissolved trail layer
 * @param {Array} favoriteTrailIds
 */
export async function fetchFavoriteTrails(favoriteTrailIds) {
  let view = app.view;
  if (!view && !app.view) return;

  if (!view.stationary) {
    await watchUtils.whenTrueOnce(view, 'stationary');
  }

  await view.when();
  await view.map.load();
  const layer = view.map.allLayers.find(function (layer) {
    return layer.title === TRAIL_DISSOLVED_TITLE;
  });
  const query = layer.createQuery();

  query.outFields = ['*'];
  query.returnGeometry = false;
  query.where = `PRIMARY_TRAIL_ID in ('${favoriteTrailIds.join("','")}')`;

  // Limit results to 100 at a time
  query.num = 10000;
  const { features } = await layer.queryFeatures(query);

  return features;
}

/**
 * Get all favorited trails from user's favorites table
 * @param {Array} favoriteRouteIds
 */
export async function fetchFavoriteRoutes(favoriteRouteIds) {
  let view = app.view;
  if (!view && !app.view) return;

  if (!view.stationary) {
    await watchUtils.whenTrueOnce(view, 'stationary');
  }

  await view.when();
  await view.map.load();
  const layer = view.map.allLayers.find(function (layer) {
    return layer.title === FEATURED_ROUTES_TITLE;
  });
  const query = layer.createQuery();

  query.outFields = ['*'];
  query.returnGeometry = false;
  query.where = `PRIMARY_TRAIL_ID in ('${favoriteRouteIds.join("','")}')`;

  // Limit results to 100 at a time
  query.num = 10000;
  const { features } = await layer.queryFeatures(query);

  return features;
}

/**
 * Adds a green dashed line graphic to the map for a trip history, zooms to recorded trail
 * @param {Object} feature
 * @returns null
 */
export async function addLineGraphicTripHistory(feature, mapView) {
  let greenDashedLine = {
    type: 'simple-line', // autocasts as new SimpleLineSymbol()
    color: '#4D7343',
    width: '3px',
    style: 'short-dash',
  };

  let whiteLine = {
    type: 'simple-line', // autocasts as new SimpleLineSymbol()
    color: '#ffffff',
    width: '5px',
    style: 'solid',
  };

  //new polyline using feature.geometry and then pass into
  let polyline = new Polyline({
    ...feature.geometry,
    spatialReference: SpatialReference.WebMercator,
  });

  let whiteLineGraphic = new Graphic({
    attributes: feature.attributes,
    geometry: polyline, // Add the geometry
    symbol: whiteLine, // Add the symbol
  });
  let greenDashedLineGraphic = new Graphic({
    attributes: feature.attributes,
    geometry: polyline, // Add the geometry
    symbol: greenDashedLine, // Add the symbol
  });

  // // Add the graphic to the view's default graphics view
  // // If adding multiple graphics, use addMany and pass in the array of graphics.
  app.view.graphics.removeAll();
  app.view.graphics.add(whiteLineGraphic);
  app.view.graphics.add(greenDashedLineGraphic);
  mapView.when(function () {
    mapView.goTo({
      target: polyline.extent, // Graphic object
    });
  });
  // });
  return polyline;
}

/**
 * Convert a geometry to a new polyline
 * @param {Object} feature
 * @returns Object
 */
export async function convertGeometryToPolyline(feature) {
  //new polyline using feature.geometry and then pass into
  return new Polyline({
    ...feature.geometry,
    spatialReference: SpatialReference.WebMercator,
  });
}

/**
 * Get all trails from trails layer by trail ids
 * @param {[Number]} trailIds
 * @returns Object
 */
export async function getAllTrailsByIds(trailIds) {
  const layer = app.webmap.allLayers.find(function (layer) {
    return layer.title === TRAIL_DISSOLVED_TITLE;
  });
  const query = layer.createQuery();
  query.outFields = ['*'];
  query.where = `PRIMARY_TRAIL_ID in ('${trailIds.join("','")}')`;
  const { features } = await layer.queryFeatures(query);
  return features;
}

/**
 * Add starting and ending image markers to a recorded trip
 * @param {Object} polyline
 * @param {Object} startingImageMarker
 * @returns Object
 */
export async function addMarkersTripHistory(polyline, startingImageMarker, endingImageMarker) {
  let startingMarker = {
    type: 'picture-marker',
    url: startingImageMarker,
    width: '20px',
    height: '30px',
  };

  let blackMarker = {
    type: 'picture-marker',
    url: endingImageMarker,
    width: '20px',
    height: '30px',
  };

  //new polyline using feature.geometry and then pass into
  let startingPoint = polyline.getPoint(0, 0);
  let endingPoint = polyline.getPoint(polyline.paths.length - 1, polyline.paths[polyline.paths.length - 1].length - 1); //The last path of the polyline
  //let endingPoint = polyline.getPoint();
  let greenStartingMarker = new Graphic({
    geometry: startingPoint, // Add the geometry
    symbol: startingMarker, // Add the symbol
  });

  let blackEndingMarker = new Graphic({
    geometry: endingPoint, // Add the geometry
    symbol: blackMarker, // Add the symbol
  });

  // // Add the graphic to the view's default graphics view
  // // If adding multiple graphics, use addMany and pass in the array of graphics.
  app.view.graphics.add(greenStartingMarker);
  app.view.graphics.add(blackEndingMarker);
}

/**
 * Remove all graphics
 * @returns null
 */
export async function removeAllGraphics() {
  app.view.graphics.removeAll();
}

/**
 * Get featured routes
 * @returns Array
 */
export async function getFeaturedRoutes() {
  console.log(app.webmap);
  const featuredRoutesLayer = app.webmap.allLayers.find(function (layer) {
    return layer.title === FEATURED_ROUTES_TITLE;
  });

  if (featuredRoutesLayer) {
    const query = featuredRoutesLayer.createQuery();
    query.where = '1=1';
    const results = await featuredRoutesLayer.queryFeatures(query);
    return results;
  } else return { features: [] };
}

/**
 * Get trailhead by trailId
 * @returns Array
 */
export async function getTrailheadFeature(trailId) {
  const trailheadsLayer = app.webmap.allLayers.find(function (layer) {
    return layer.title === TRAILHEAD_TITLE;
  });
  const query = trailheadsLayer.createQuery();
  query.where = `Trail_ID = ${trailId}`;
  const results = await trailheadsLayer.queryFeatures(query);
  return results;
}

/**
 * Get trailhead by objectId
 * @returns Array
 */
export async function getTrailheadFeatureByObjectId(objectId) {
  const trailheadsLayer = app.webmap.allLayers.find(function (layer) {
    return layer.title === TRAILHEAD_TITLE;
  });
  const query = trailheadsLayer.createQuery();
  query.outFields = ['*'];
  query.where = `OBJECTID = ${objectId}`;
  const results = await trailheadsLayer.queryFeatures(query);
  return results;
}

/**
 * Get trails dissolved layer
 * @returns Object
 */
export function getTrailsDissolvedLayer() {
  return app.webmap.allLayers.find(function (layer) {
    return layer.title === TRAIL_DISSOLVED_TITLE;
  });
}

export function setAppUsername(userInfo) {
  if (userInfo && userInfo.user && userInfo.user.username) {
    app.username = userInfo.user.username;
  } else {
    app.username = null;
  }
}

export function getAppUsername() {
  return app.username ? app.username : null;
}
